import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, dashboardPathForRole } from "../lib/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Shield, LayoutDashboard, LogOut } from "lucide-react";

function RoleBadge({ role }) {
  const labels = { user: "Usuario", admin: "Administrador", reviewer: "Revisor" };
  return (
    <span
      data-testid="header-role-badge"
      className="hidden sm:inline-block text-[10px] uppercase tracking-widest font-medium border border-gray-300 px-2 py-[2px] text-gray-600"
    >
      {labels[role] || role}
    </span>
  );
}

/**
 * BrandLockup
 * Logo oficial JustSapiens Procure (horizontal positivo) sobre fondo claro.
 * En pantallas muy pequeñas mostramos solo el símbolo para optimizar el espacio.
 */
function BrandLockup() {
  return (
    <Link
      to="/"
      data-testid="header-logo"
      className="flex items-center gap-2 group"
      aria-label="JustSapiens Procure — Inicio"
    >
      {/* Símbolo solo en mobile (xs) */}
      <img
        src="/brand/symbol-positive.svg"
        alt=""
        aria-hidden="true"
        className="h-10 w-10 sm:hidden object-contain shrink-0"
      />
      {/* Logo horizontal en sm+ */}
      <img
        src="/brand/logo-horizontal-positive.png"
        alt="JustSapiens Procure"
        className="hidden sm:block h-12 w-auto object-contain shrink-0"
        loading="eager"
        decoding="async"
      />
    </Link>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const onLogout = () => {
    logout();
    navigate("/");
  };

  const initials = (user?.name || user?.email || "U")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header
      data-testid="site-header"
      className="border-b border-gray-200 bg-white/95 backdrop-blur-sm sticky top-0 z-30"
    >
      <div className="container-prose flex items-center justify-between h-20">
        <BrandLockup />

        <nav className="flex items-center gap-3">
          {!user && location.pathname === "/" && (
            <>
              <Link
                to="/login"
                data-testid="header-login-link"
                className="hidden sm:inline text-sm text-gray-700 hover:text-brand-burgundy transition-colors"
              >
                Iniciar sesión
              </Link>
              <Button
                asChild
                size="sm"
                className="rounded-sm bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
                data-testid="header-register-button"
              >
                <Link to="/register">Crear cuenta</Link>
              </Button>
            </>
          )}

          {user && (
            <>
              <Link
                to={dashboardPathForRole(user.role)}
                data-testid="header-dashboard-link"
                className="hidden sm:inline text-sm text-gray-700 hover:text-brand-burgundy transition-colors"
              >
                Mi panel
              </Link>
              <RoleBadge role={user.role} />
              <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="header-user-menu-trigger"
                    className="flex items-center gap-2 border border-gray-300 px-2 py-1 hover:border-brand-burgundy transition-colors rounded-sm"
                  >
                    <span className="h-7 w-7 grid place-items-center bg-brand-burgundy text-white text-xs font-semibold rounded-sm">
                      {initials}
                    </span>
                    <span className="hidden md:inline text-sm text-gray-800 max-w-[160px] truncate">
                      {user.email}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-sm">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">{user.name}</span>
                      <span className="text-xs text-gray-500 truncate">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="header-menu-dashboard"
                    onClick={() => navigate(dashboardPathForRole(user.role))}
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Ir a mi panel
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="header-menu-security"
                    onClick={() => navigate("/profile/security")}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Seguridad
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="header-logout-button"
                    onClick={onLogout}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
